import styled, { css } from 'styled-components';

/* ─── Tuiles de synthèse ─── */
export const StatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
`;

export const StatTile = styled.div`
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-left: 3px solid ${(p) => p.theme[p.$tone] || p.theme.accent};
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow: ${(p) => p.theme.shadowSm};
    padding: 14px 16px;

    .label {
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${(p) => p.theme.text2};
    }
    .value {
        margin-top: 4px;
        font-size: 1.6rem;
        font-weight: 800;
        line-height: 1.1;
        font-variant-numeric: tabular-nums;
        color: ${(p) => p.theme.text};
    }
    .hint {
        margin-top: 2px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
    }
    /* Ligne actionnable, au-dessus des indices neutres : c'est elle qu'on doit
       lire en premier quand elle est présente. */
    .alert {
        margin-top: 4px;
        font-size: ${(p) => p.theme.type.caption};
        font-weight: 700;
        color: ${(p) => p.theme.danger};
    }
`;

/* ═══════════════ Fiche appareil : structure par sections ═══════════════
   Reprise de la disposition du projet SI-Securisation — bandeau d'identité,
   tuiles d'état cliquables, navigation latérale groupée, une section à la fois.
   Le découpage est par THÈME et non par système source : personne ne cherche
   « ce que dit Intune », on cherche le numéro de série ou l'état de conformité.
   Notre palette et nos composants restent inchangés. */

export const DeviceHero = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow: ${(p) => p.theme.shadowSm};
    padding: 16px 18px;
    margin-bottom: 12px;

    /* Objet absent de l'annuaire : le bandeau le dit en mots, la mise en forme
       le dit avant qu'on lise. Le portrait passe en gris et le nom perd son
       contraste plein — la fiche se donne pour ce qu'elle est, une archive.
       Le liseré ambre reste le seul aplat de couleur : teinter tout le bandeau
       ferait crier une fin de contrat, qui est un événement normal. */
    ${(p) => p.$absent && css`
        border-left: 3px solid ${p.theme.warn};

        > i, > img {
            filter: grayscale(1);
            opacity: 0.65;
        }
        .name {
            color: ${p.theme.text2};
        }
    `}

    > i {
        width: 44px;
        height: 44px;
        flex-shrink: 0;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        background: ${(p) => p.theme.accentSoft};
        color: ${(p) => p.theme.accentText};
    }

    .eyebrow {
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.09em;
        color: ${(p) => p.theme.text3};
    }
    .name {
        margin: 1px 0 0;
        font-size: 1.25rem;
        font-weight: 800;
        color: ${(p) => p.theme.text};
        line-height: 1.2;
    }
    /* Faits d'identité en ligne : lus une fois, ce n'est pas du signal à
       scanner — donc pas de tuiles. */
    .facts {
        margin-top: 3px;
        display: flex;
        flex-wrap: wrap;
        gap: 4px 18px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
    }
    .facts b {
        color: ${(p) => p.theme.text2};
        font-weight: 600;
    }
`;

/* Verdict de posture. Le ton porte le jugement, la liste des raisons le
   justifie : un verdict sans motif se conteste, un verdict motivé se traite. */
export const PostureBadge = styled.div`
    margin-left: auto;
    text-align: right;
    padding: 8px 14px;
    border-radius: ${(p) => p.theme.radius.sm};
    border: 1px solid ${(p) => p.theme[p.$tone] || p.theme.border};
    background: ${(p) => p.theme[`${p.$tone}Soft`] || p.theme.surface2};

    .verdict {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 7px;
        font-size: 0.82rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: ${(p) => p.theme[p.$tone] || p.theme.text2};
    }
    .reasons {
        margin-top: 1px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme[p.$tone] || p.theme.text3};
        opacity: 0.85;
    }
`;

export const HealthStrip = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(185px, 1fr));
    gap: 10px;
    margin-bottom: 14px;
`;

/* Tuile d'état CLIQUABLE : elle mène à sa section. Un compteur qui ne conduit
   nulle part oblige à chercher où lire le détail. */
export const HealthTile = styled.button`
    display: flex;
    align-items: center;
    gap: 11px;
    text-align: left;
    font-family: inherit;
    cursor: pointer;
    padding: 12px 14px;
    background: ${(p) => (p.$active ? p.theme.accentSoft : p.theme.surface)};
    border: 1px solid ${(p) => (p.$active ? p.theme.accent : p.theme.border)};
    border-left: 3px solid ${(p) => p.theme[p.$tone] || p.theme.accent};
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow: ${(p) => p.theme.shadowSm};
    transition: background 0.12s ease, border-color 0.12s ease;

    &:hover { background: ${(p) => p.theme.surface2}; }

    > i {
        width: 30px;
        height: 30px;
        flex-shrink: 0;
        border-radius: ${(p) => p.theme.radius.sm};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.05rem;
        background: ${(p) => p.theme[`${p.$tone}Soft`] || p.theme.surface3};
        color: ${(p) => p.theme[p.$tone] || p.theme.text2};
    }

    .value {
        font-size: 1.05rem;
        font-weight: 800;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
        color: ${(p) => p.theme.text};
    }
    .label {
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
    }
`;

export const DeviceLayout = styled.div`
    display: grid;
    grid-template-columns: 208px minmax(0, 1fr);
    gap: 14px;
    align-items: start;

    /* Sous 900 px la navigation passe au-dessus du contenu : la garder en
       colonne ne laisserait pas assez de largeur aux tableaux. */
    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

export const DeviceNav = styled.nav`
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow: ${(p) => p.theme.shadowSm};
    padding: 7px;

    .group {
        padding: 13px 10px 4px;
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: ${(p) => p.theme.text3};
    }
`;

export const NavItem = styled.button`
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 7px 10px;
    font-family: inherit;
    font-size: ${(p) => p.theme.type.table};
    text-align: left;
    cursor: pointer;
    border: none;
    border-left: 2px solid ${(p) => (p.$active ? p.theme.accent : 'transparent')};
    border-radius: ${(p) => (p.$active ? `0 ${p.theme.radius.sm} ${p.theme.radius.sm} 0` : p.theme.radius.sm)};
    background: ${(p) => (p.$active ? p.theme.accentSoft : 'transparent')};
    color: ${(p) => (p.$active ? p.theme.accentText : p.theme.text2)};
    font-weight: ${(p) => (p.$active ? 600 : 400)};

    &:hover { background: ${(p) => (p.$active ? p.theme.accentSoft : p.theme.surface2)}; }

    > i {
        flex-shrink: 0;
        width: 16px;
        text-align: center;
        font-size: 1rem;
        opacity: 0.8;
    }
    /* Décompte neutre : combien d'éléments dans la section. Ton discret, à
       l'opposé de .badge qui signale quelque chose à traiter. */
    .count {
        margin-left: auto;
        font-size: 0.68rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: ${(p) => (p.$active ? p.theme.accentText : p.theme.text3)};
    }
    /* Compteur d'alerte : il n'apparaît que s'il y a quelque chose à traiter. */
    .badge {
        margin-left: auto;
        font-size: 0.68rem;
        font-weight: 700;
        padding: 1px 7px;
        border-radius: ${(p) => p.theme.radius.pill};
        background: ${(p) => p.theme.dangerSoft};
        color: ${(p) => p.theme.danger};
    }
`;

/* ─── Fiches de caractéristiques ───
   Grille de paires libellé/valeur, deux colonnes. Volontairement PAS des cartes
   à ombre : ce sont des références qu'on consulte, pas des objets à distinguer. */
export const SpecGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 14px;
    align-content: start;
`;

export const SpecFields = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 13px 18px;

    > div { min-width: 0; }
    > div.full { grid-column: 1 / -1; }

    dt {
        margin: 0 0 2px;
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: ${(p) => p.theme.text3};
    }
    dd {
        margin: 0;
        font-size: ${(p) => p.theme.type.body};
        font-weight: 600;
        color: ${(p) => p.theme.text};
        overflow-wrap: anywhere;
    }
    dd.mono {
        font-family: 'Cascadia Code', Consolas, monospace;
        font-size: 0.78rem;
        font-weight: 400;
    }
    dd.ok { color: ${(p) => p.theme.ok}; }
    dd.danger { color: ${(p) => p.theme.danger}; }
    dd.empty { color: ${(p) => p.theme.text3}; font-weight: 400; }
`;

/* Occupation disque : une barre se lit d'un coup d'œil là où deux nombres
   demandent une soustraction mentale. Le ton passe à l'alerte au-delà de 90 %. */
export const StorageBar = styled.div`
    margin-top: 6px;

    .track {
        height: 6px;
        border-radius: ${(p) => p.theme.radius.pill};
        background: ${(p) => p.theme.surface3};
        overflow: hidden;
    }
    .fill {
        height: 100%;
        width: ${(p) => p.$percent}%;
        background: ${(p) => (p.$percent >= 90 ? p.theme.danger : p.$percent >= 75 ? p.theme.warn : p.theme.accent)};
    }
    .legend {
        display: flex;
        justify-content: space-between;
        margin-top: 4px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
        font-variant-numeric: tabular-nums;
    }
`;

export const MachineTag = styled.span`
    display: inline-block;
    margin: 0 5px 5px 0;
    padding: 2px 8px;
    font-family: 'Cascadia Code', Consolas, monospace;
    font-size: 0.7rem;
    border-radius: ${(p) => p.theme.radius.sm};
    background: ${(p) => p.theme.surface3};
    border: 1px solid ${(p) => p.theme.border};
    color: ${(p) => p.theme.text2};
`;

/* ═══════════════ Page Applications ═══════════════
   Maître-détail : la liste ne bouge pas au clic, le détail par appareil s'ouvre
   dans un rail latéral collant. Un accordéon en place repousserait toute la
   liste à chaque sélection. */

export const AppLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 540px;
    gap: 14px;
    align-items: start;

    /* Le tableau du détail a besoin de largeur : sous ce seuil, le rail passe
       dessous plutôt que de comprimer les deux. */
    @media (max-width: 1400px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

export const AppRail = styled.div`
    position: sticky;
    top: 16px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
`;

export const AppRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 13px;
    cursor: pointer;
    border: 1px solid ${(p) => (p.$selected ? p.theme.accent : p.theme.border)};
    border-left: 3px solid ${(p) => (p.$selected ? p.theme.accent : 'transparent')};
    border-radius: ${(p) => p.theme.radius.sm};
    background: ${(p) => (p.$selected ? p.theme.accentSoft : p.theme.surface)};
    margin-bottom: 7px;
    transition: background 0.12s ease, border-color 0.12s ease;

    &:hover { background: ${(p) => (p.$selected ? p.theme.accentSoft : p.theme.surface2)}; }
    &:last-child { margin-bottom: 0; }

    .body { flex: 1; min-width: 0; }
    .name {
        font-size: ${(p) => p.theme.type.body};
        font-weight: 600;
        color: ${(p) => p.theme.text};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .meta {
        margin-top: 1px;
        display: flex;
        flex-wrap: wrap;
        gap: 3px 10px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
    }
    .count {
        flex-shrink: 0;
        text-align: right;
        font-variant-numeric: tabular-nums;
    }
    .count b {
        display: block;
        font-size: ${(p) => p.theme.type.body};
        font-weight: 700;
        color: ${(p) => p.theme.text};
    }
    .count span {
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
    }
`;

/* Pastille à l'initiale, teintée de façon déterministe depuis le nom. Intune ne
   sert pas les icônes sur une collection — mesuré le 2026-09-03, `largeIcon`
   revient vide sur les 244 applications quand on interroge la liste, et n'est
   renseigné qu'appareil par appareil. La pastille n'est donc pas un repli en
   attendant mieux : c'est le seul rendu possible sans 244 requêtes. */
const TEINTES = ['accent', 'ok', 'warn', 'danger', 'rssi'];

export const AppIcon = styled.span`
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: ${(p) => p.theme.radius.sm};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    font-weight: 800;
    text-transform: uppercase;
    background: ${(p) => p.theme[`${TEINTES[p.$seed % TEINTES.length]}Soft`]};
    color: ${(p) => p.theme[TEINTES[p.$seed % TEINTES.length]]};
`;

/* Répartition du déploiement en une barre : installées, échecs, en attente,
   non installées. Les non applicables sont exclues — un appareil hors périmètre
   n'a jamais été censé recevoir l'application, l'inclure gonflerait le
   dénominateur avec des postes qui ne sont pas concernés. */
export const InstallBar = styled.div`
    display: flex;
    height: 5px;
    margin-top: 5px;
    border-radius: ${(p) => p.theme.radius.pill};
    overflow: hidden;
    background: ${(p) => p.theme.surface3};

    span { height: 100%; }
    span.installed { background: ${(p) => p.theme.ok}; }
    span.failed { background: ${(p) => p.theme.danger}; }
    span.pending { background: ${(p) => p.theme.accent}; }
    span.notInstalled { background: ${(p) => p.theme.borderStrong}; }
`;

// Pastille de filtre par état, cliquable — le compteur reste visible même
// lorsque le filtre est inactif, pour qu'on sache ce qu'on gagnerait à cliquer.
export const StateChip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 11px;
    font-family: inherit;
    font-size: ${(p) => p.theme.type.caption};
    cursor: pointer;
    border-radius: ${(p) => p.theme.radius.pill};
    border: 1px solid ${(p) => (p.$active ? p.theme[p.$tone] || p.theme.accent : p.theme.border)};
    background: ${(p) => (p.$active ? p.theme[`${p.$tone}Soft`] || p.theme.accentSoft : p.theme.surface)};
    color: ${(p) => (p.$active ? p.theme[p.$tone] || p.theme.accentText : p.theme.text2)};

    &:hover { background: ${(p) => (p.$active ? p.theme[`${p.$tone}Soft`] : p.theme.surface2)}; }

    i.dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${(p) => p.theme[p.$tone] || p.theme.text3};
    }
    b { font-variant-numeric: tabular-nums; font-weight: 700; }
`;

/* ─── Répartition par sévérité ─── */
export const SeverityBar = styled.div`
    display: flex;
    height: 10px;
    border-radius: ${(p) => p.theme.radius.pill};
    overflow: hidden;
    background: ${(p) => p.theme.surface3};
    margin-bottom: 10px;
`;

// La couleur vient du thème actif via $toneKey ('danger', 'warn'…) : utiliser
// une valeur en dur figerait la barre sur la palette claire en thème sombre.
export const SeveritySegment = styled.span`
    height: 100%;
    width: ${(p) => p.$width}%;
    background: ${(p) => p.theme[p.$toneKey] || p.theme.text3};
    transition: width 0.2s ease;
`;

export const SeverityLegend = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: ${(p) => p.theme.type.caption};
    color: ${(p) => p.theme.text2};

    span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    strong {
        color: ${(p) => p.theme.text};
        font-variant-numeric: tabular-nums;
    }
`;

export const LegendDot = styled.i`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${(p) => p.theme[p.$toneKey] || p.theme.text3};
`;

/* ─── Barre de filtres ─── */
export const FilterBar = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;

    label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text2};
        cursor: pointer;
        white-space: nowrap;
    }
    input[type='checkbox'] {
        accent-color: ${(p) => p.theme.accent};
        width: 15px;
        height: 15px;
        cursor: pointer;
    }
`;

/* ─── Ligne de tableau cliquable ─── */
export const ClickableRow = styled.tr`
    cursor: pointer;

    td:first-child {
        font-weight: 600;
        color: ${(p) => p.theme.accentText};
    }
`;

export const MonoCell = styled.span`
    font-family: 'Cascadia Code', Consolas, monospace;
    font-size: 0.78rem;
`;

/* ─── Inventaires d'origine d'un appareil ───
   Un poste peut être connu d'Entra, d'Intune et de Defender indépendamment : 849
   appareils Entra actifs ne sont pas onboardés dans Defender, et tous les
   appareils Entra ne sont pas gérés par Intune. Trois initiales valent mieux que
   trois colonnes de « oui / non » : la lecture est immédiate sur 50 lignes. */
export const SourceTags = styled.div`
    display: inline-flex;
    gap: 4px;

    span {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 20px;
        border-radius: ${(p) => p.theme.radius.sm};
        font-size: 0.66rem;
        font-weight: 800;
        cursor: default;
        border: 1px solid ${(p) => p.theme.border};
        background: ${(p) => p.theme.surface2};
        color: ${(p) => p.theme.text3};
    }

    /* Présent : plein et contrasté. Absent : creux et effacé — l'œil doit
       repérer le manque, pas avoir à lire chaque badge. */
    span.on {
        border-color: transparent;
        background: ${(p) => p.theme.accent};
        color: #fff;
    }
`;

/* ─── Vue détail ─── */
export const DetailMeta = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    padding: 14px 0 0;
    margin-top: 14px;
    border-top: 1px solid ${(p) => p.theme.border};

    > div > .label {
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${(p) => p.theme.text3};
    }
    > div > .value {
        margin-top: 3px;
        font-size: ${(p) => p.theme.type.body};
        color: ${(p) => p.theme.text};
        font-variant-numeric: tabular-nums;
    }
`;

// Les chemins Windows sont longs : monospace, une ligne par chemin, et le
// conteneur borne sa hauteur en défilant plutôt qu'en élargissant le tableau.
//
// La hauteur doit valoir un nombre ENTIER de lignes. Avec 96 px et une hauteur
// de ligne héritée de 17,28 px, la limite tombait à 5,55 lignes : la sixième
// était coupée en deux et donnait l'impression d'un texte écrasé. 6 × 18 px
// tombe juste, donc le défilement démarre toujours sur une ligne complète.
//
// Volumétrie mesurée le 2026-08-27 : 95 % des cellules ont 5 chemins ou moins
// (médiane 1), donc six lignes suffisent à tout montrer dans la quasi-totalité
// des cas. Les rares cellules à 30 ou 50 chemins défilent.
export const PathList = styled.div`
    /* PAS de display: flex ici. En colonne flex avec une max-height, les span
       sont des éléments flex à flex-shrink: 1 par défaut : au-delà de la hauteur
       disponible le moteur les COMPRIME pour les faire tenir au lieu de laisser
       déborder. Les glyphes se chevauchaient, et overflow-y n'y changeait rien
       puisqu'après compression le contenu tient. C'était la cause réelle de
       l'écrasement vertical. Un bloc simple déborde et défile normalement. */
    max-width: 460px;
    line-height: 18px;
    /* 6 lignes en repli, 20 une fois déplié. */
    max-height: ${(p) => (p.$expanded ? '360px' : '108px')};
    overflow-y: auto;
    font-family: 'Cascadia Code', Consolas, monospace;
    font-size: 0.72rem;
    color: ${(p) => p.theme.text2};

    span {
        display: block;
        /* Troncature à droite plutôt que repli : un chemin fait 85 caractères
           en moyenne, le replier doublerait la hauteur de chaque ligne. Le
           chemin entier reste lisible : PathCell pose un attribut title sur
           chaque ligne. */
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

// Compteur cliquable qui remplace une longue liste de chemins.
export const PathToggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 9px 2px 5px;
    background: ${(p) => p.theme.surface2};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.pill};
    font-family: inherit;
    font-size: 0.72rem;
    font-weight: 700;
    color: ${(p) => p.theme.text2};
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.12s ease, color 0.12s ease;

    &:hover {
        background: ${(p) => p.theme.surface3};
        color: ${(p) => p.theme.text};
    }

    > i {
        font-size: 1rem;
        flex-shrink: 0;
    }
`;

export const Muted = styled.span`
    color: ${(p) => p.theme.text3};
`;

export const Description = styled.p`
    margin: 0;
    font-size: ${(p) => p.theme.type.body};
    line-height: 1.6;
    color: ${(p) => p.theme.text2};
`;

// Les résumés Microsoft arrivent en un bloc de 1 100 caractères en moyenne, où
// « Summary: », « Impact: », « AdditionalInformation: » et « Remediation: » se
// suivent sans respiration. Découpés à la lecture, ils deviennent quatre
// sections titrées — d'où cet empilement plutôt qu'un paragraphe unique.
export const DescriptionSections = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

/* ─── Recommandations ─── */
export const RecoList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const RecoItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 11px 13px;
    background: ${(p) => (p.$actionable ? p.theme.okSoft : p.theme.surface2)};
    border-left: 3px solid ${(p) => (p.$actionable ? p.theme.ok : p.theme.border)};
    border-radius: ${(p) => p.theme.radius.sm};

    > i {
        font-size: 1.1rem;
        flex-shrink: 0;
        margin-top: 1px;
        color: ${(p) => (p.$actionable ? p.theme.ok : p.theme.text3)};
    }

    .body { flex: 1; min-width: 0; }

    .name {
        font-size: ${(p) => p.theme.type.body};
        font-weight: 600;
        color: ${(p) => p.theme.text};
    }

    .meta {
        margin-top: 3px;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
    }
`;

/* ─── Conformité Intune ─── */
const POLICY_TONE = {
    compliant: 'ok',
    nonCompliant: 'danger',
    error: 'danger',
    conflict: 'warn',
    notApplicable: 'text3',
    inGracePeriod: 'warn',
};

export const PolicyBlock = styled.div`
    border: 1px solid ${(p) => p.theme.border};
    border-left: 3px solid ${(p) => p.theme[POLICY_TONE[p.$state]] || p.theme.border};
    border-radius: ${(p) => p.theme.radius.sm};
    margin-bottom: 8px;
    overflow: hidden;

    &:last-child { margin-bottom: 0; }
`;

/* Largeur de la gouttière d'état, partagée par l'en-tête d'une stratégie et les
   lignes de ses paramètres. C'est elle qui fait tomber le nom d'un paramètre
   exactement sous le nom de sa stratégie : le badge d'état a une largeur qui
   dépend de son libellé (« Conforme » / « Non applicable »), donc sans piste
   commune les deux niveaux ne s'alignent jamais. Une valeur écrite en dur de
   chaque côté finirait par diverger, d'où la constante. */
const STATE_COLUMN = '130px';

// Réserve la gouttière sans étirer le badge : en conteneur flex, l'enfant garde
// sa largeur naturelle sur l'axe principal.
export const StateCell = styled.span`
    flex: 0 0 ${STATE_COLUMN};
    display: flex;
    align-items: center;
`;

export const PolicyHeader = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 13px;
    background: ${(p) => p.theme.surface2};
    border: none;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover { background: ${(p) => p.theme.surface3}; }

    .name {
        flex: 1;
        min-width: 0;
        font-size: ${(p) => p.theme.type.body};
        font-weight: 600;
        color: ${(p) => p.theme.text};
    }
    > i {
        flex-shrink: 0;
        font-size: 1.15rem;
        color: ${(p) => p.theme.text3};
        transition: transform 0.15s ease;
        transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
    }
`;

export const SettingRow = styled.div`
    display: grid;
    grid-template-columns: ${STATE_COLUMN} minmax(0, 1fr) auto;
    align-items: baseline;
    gap: 10px;
    padding: 6px 13px;
    font-size: ${(p) => p.theme.type.caption};
    border-top: 1px solid ${(p) => p.theme.border};

    /* Le badge d'état est un inline-flex : dans une grille il s'étire par défaut
       sur toute la piste, ce qui le rendait plus large que celui de la stratégie
       parente — un enfant paraissait ainsi plus fort que son parent. On le laisse
       épouser son contenu, la piste ne servant qu'à aligner. */
    > *:first-child { justify-self: start; }

    .value {
        font-family: 'Cascadia Code', Consolas, monospace;
        font-size: 0.72rem;
        color: ${(p) => p.theme.text3};
        text-align: right;
    }
    .name {
        color: ${(p) => p.theme.text};
        overflow-wrap: anywhere;
    }
`;

export const TargetVersion = styled.span`
    font-family: 'Cascadia Code', Consolas, monospace;
    font-size: 0.8rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: ${(p) => p.theme.radius.sm};
    background: ${(p) => p.theme.ok};
    color: #fff;
    white-space: nowrap;
    flex-shrink: 0;
`;

/* ─── Pagination ─── */
export const Pager = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding-top: 12px;
    margin-top: 10px;
    border-top: 1px solid ${(p) => p.theme.border};
    font-size: ${(p) => p.theme.type.caption};
    color: ${(p) => p.theme.text2};

    .spacer { flex: 1; }
    strong {
        color: ${(p) => p.theme.text};
        font-variant-numeric: tabular-nums;
    }
`;

/* ─── Autocomplétion (vue 360°) ─── */
export const SuggestionsList = styled.ul`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 50;
    margin: 0;
    padding: 4px;
    list-style: none;
    max-height: 300px;
    overflow-y: auto;
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.sm};
    box-shadow: ${(p) => p.theme.shadowMd};
`;

export const SuggestionItem = styled.li`
    padding: 7px 10px;
    font-size: ${(p) => p.theme.type.table};
    border-radius: ${(p) => p.theme.radius.sm};
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover { background: ${(p) => p.theme.surface3}; }
`;

/* ═══════════════════════ Historique de conformité ═══════════════════════ */

// Ligne de filtres au-dessus des courbes, jamais à côté : le lecteur règle le
// périmètre puis lit, il ne fait pas les deux en même temps.
export const HistoFiltres = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 16px;
`;

export const HistoSelect = styled.select`
    font-family: inherit;
    font-size: ${(p) => p.theme.type.caption};
    color: ${(p) => p.theme.text};
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.sm};
    padding: 6px 9px;

    &:focus-visible {
        outline: 2px solid ${(p) => p.theme.accent};
        outline-offset: -1px;
    }
`;

// Deux courbes côte à côte, chacune avec SA propre échelle. Un taux de
// conformité (0 à 100 %, haut = bon) et un score d'exposition (haut = mauvais)
// sur un axe commun produiraient un graphique qui ment.
// Colonnes PLAFONNÉES à 552 px, et non en 1fr.
//
// Le détail n'affiche plus qu'une seule courbe : en 1fr elle s'étirait sur toute
// la largeur de la carte, et comme le SVG est en width:100% avec un viewBox de
// 520x150, sa hauteur doublait au passage. Le plafond lui rend la taille qu'elle
// avait quand deux courbes se partageaient la ligne — 520 px de tracé plus les
// marges intérieures de la carte.
export const HistoGrille = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 552px));
    justify-content: start;
    gap: 14px;
`;

export const HistoCarte = styled.div`
    position: relative;
    background: ${(p) => p.theme.surface2};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.md};
    padding: 14px 16px;
`;

export const HistoEntete = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 10px;

    & .titre {
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${(p) => p.theme.text3};
    }

    & .valeur {
        font-size: 1.5rem;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
        color: ${(p) => p.theme.text};
    }
`;

export const HistoSvg = styled.svg`
    display: block;
    width: 100%;
    /* Jamais agrandi au-delà de son viewBox : au-dessus de 520 px le tracé
       s'épaissit sans rien gagner en lisibilité, et l'infobulle — positionnée en
       pourcentage de la carte — se décalerait du point survolé. */
    max-width: 520px;
    height: auto;
    overflow: visible;
`;

// Infobulle suivant le point survolé. Positionnée en absolu dans HistoCarte,
// donc au-dessus du tracé sans le décaler.
export const HistoBulle = styled.div`
    position: absolute;
    transform: translate(-50%, -100%);
    pointer-events: none;
    z-index: 2;
    padding: 6px 9px;
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.borderStrong};
    border-radius: ${(p) => p.theme.radius.sm};
    box-shadow: ${(p) => p.theme.shadowMd};
    font-size: ${(p) => p.theme.type.caption};
    white-space: nowrap;

    & strong {
        font-variant-numeric: tabular-nums;
    }
`;

// Les compteurs d'alerte. Deux d'entre eux sont des désaccords entre le verdict
// d'Entra et le détail des paramètres : ils portent un ton, les autres non.
export const HistoAlertes = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 10px;
    margin-top: 14px;
`;

export const HistoAlerte = styled.div`
    padding: 10px 12px;
    border-radius: ${(p) => p.theme.radius.sm};
    background: ${(p) => (p.$tone === 'danger' ? p.theme.dangerSoft
        : p.$tone === 'warn' ? p.theme.warnSoft
        : p.theme.surface3)};

    & .valeur {
        font-size: 1.15rem;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
        color: ${(p) => (p.$tone === 'danger' ? p.theme.danger
            : p.$tone === 'warn' ? p.theme.warn
            : p.theme.text)};
    }

    & .libelle {
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text2};
        line-height: 1.35;
    }
`;

// Tuile de tendance : même famille visuelle que StatTile — trait de ton à
// gauche, mêmes marges — mais cliquable, et porteuse d'un aperçu de courbe.
// C'est un bouton et non un div : ce qui ouvre un détail doit se voir et se
// prendre au clavier.
export const TuileTendance = styled.button`
    display: block;
    width: 100%;
    text-align: left;
    font-family: inherit;
    cursor: pointer;
    background: ${(p) => (p.$ouvert ? p.theme.accentSoft : p.theme.surface)};
    border: 1px solid ${(p) => (p.$ouvert ? p.theme.accent : p.theme.border)};
    border-left: 3px solid ${(p) => p.theme[p.$tone] || p.theme.accent};
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow: ${(p) => p.theme.shadowSm};
    padding: 14px 16px;
    transition: background 0.12s ease, border-color 0.12s ease;

    &:hover { background: ${(p) => p.theme.surface2}; }
    &:focus-visible { outline: 2px solid ${(p) => p.theme.accent}; outline-offset: 1px; }

    .label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        font-size: ${(p) => p.theme.type.label};
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${(p) => p.theme.text2};
    }
    .value {
        margin-top: 4px;
        font-size: 1.6rem;
        font-weight: 800;
        line-height: 1.1;
        font-variant-numeric: tabular-nums;
        color: ${(p) => p.theme.text};
    }
    .hint {
        margin-top: 2px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
    }
`;

// Rangée « valeur + variation + aperçu de courbe ». L'aperçu n'a ni axe ni
// graduation : il donne la forme, pas la mesure — celle-ci est dans le détail.
export const TuilePied = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px;
    margin-top: 6px;

    & svg {
        display: block;
        flex-shrink: 0;
    }
`;

export const TuileVariation = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: ${(p) => p.theme.type.caption};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.theme[p.$tone] || p.theme.text3};
`;

// Lien externe réduit à son icône, pour les bandeaux d'identité.
//
// Une ancre nue y prend le bleu souligné du navigateur — aucun style d'ancre
// global n'existe dans l'application — et jure au milieu de faits en 0,78 rem.
// Ici l'icône porte la marque, la couleur vient du thème.
export const LienIcone = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${(p) => (p.$mini ? '22px' : '24px')};
    height: ${(p) => (p.$mini ? '22px' : '24px')};
    border-radius: ${(p) => p.theme.radius.sm};
    background: ${(p) => (p.$mini ? 'transparent' : p.theme.surface3)};
    color: ${(p) => (p.$mini ? p.theme.text3 : p.theme.text2)};
    font-size: ${(p) => (p.$mini ? '1rem' : '1.05rem')};
    text-decoration: none;
    transition: background 0.12s ease, color 0.12s ease;

    &:hover {
        background: ${(p) => p.theme.accentSoft};
        color: ${(p) => p.theme.accentText};
    }
    &:focus-visible {
        outline: 2px solid ${(p) => p.theme.accent};
        outline-offset: 1px;
    }
`;

/* ─── Actions portées par la cellule du nom ───
   Une colonne « Ouvrir dans » mettait sur le même plan un fait et un geste, et
   prélevait 90 px sur chaque ligne pour deux icônes. Les destinations vivent
   maintenant à côté du nom qu'elles ouvrent.

   Reculées au repos (le nom reste le premier mot lu), pleines au survol de la
   ligne. Toujours pleines là où il n'y a pas de survol — écran tactile — et au
   focus clavier, sinon la tabulation mènerait sur un lien invisible. */
export const CelluleNom = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;

    .nom {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const ActionsLigne = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 1px;
    flex-shrink: 0;
    opacity: 0.4;
    transition: opacity 0.12s ease;

    tr:hover & { opacity: 1; }
    &:focus-within { opacity: 1; }
    @media (hover: none) { opacity: 1; }
`;

/* ─── En-tête de colonne triable ───
   Un bouton et non un `th` cliquable : le tri est une action, elle doit être
   atteignable au clavier et annoncée comme telle. */
export const TriEntete = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0;
    margin: 0;
    background: none;
    border: 0;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: inherit;
    cursor: pointer;
    white-space: nowrap;

    i {
        font-size: 0.95rem;
        opacity: 0.45;
        transition: opacity 0.12s ease;
    }
    &:hover i { opacity: 1; }
    &[data-trie='true'] {
        color: ${(p) => p.theme.accentText};
    }
    &[data-trie='true'] i { opacity: 1; }
    &:focus-visible {
        outline: 2px solid ${(p) => p.theme.accent};
        outline-offset: 2px;
        border-radius: 2px;
    }
`;

/* ─── Lien de texte interne ───
   Pour un identifiant qui EST la destination — un UPN qui mène à sa fiche. Le
   bouton d'icône convient à côté d'un nom ; ici c'est le texte lui-même qu'on
   clique, et une ancre nue y prendrait le bleu souligné du navigateur.

   Souligné seulement au survol : dans une grille de faits, dix soulignements
   permanents feraient un bruit de formulaire. */
export const LienTexte = styled.a`
    color: ${(p) => p.theme.accentText};
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
    &:focus-visible {
        outline: 2px solid ${(p) => p.theme.accent};
        outline-offset: 2px;
        border-radius: 2px;
    }
`;

/* ─── Portrait de petite taille ───
   Les photos sont désormais collectées en 120×120 : les afficher à 26 px dans
   un tableau ou à 34 px dans un organigramme ne coûte plus rien. Le liseré
   intérieur compense le fond des photos, souvent clair, sur le thème sombre. */
export const AvatarMini = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${(p) => p.$taille || '26px'};
    height: ${(p) => p.$taille || '26px'};
    border-radius: 50%;
    overflow: hidden;
    background: ${(p) => p.theme.accentSoft};
    color: ${(p) => p.theme.accentText};
    font-size: ${(p) => (parseInt(p.$taille || '26', 10) >= 34 ? '0.8rem' : '0.66rem')};
    font-weight: 800;
    letter-spacing: 0.02em;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        box-shadow: inset 0 0 0 1px ${(p) => p.theme.border};
    }
`;

/* ─── Organigramme ───
   Trois niveaux — le manager, le compte consulté, ses rattachés — et non plus
   un seul. Une fiche d'organisation qui ne montre pas le manager est à moitié
   vide, et l'information existe déjà dans la fiche.

   Une grille et non un arbre dessiné : au-delà de quatre ou cinq rattachés, un
   arbre centré déborde de la colonne. Le plus grand manager du parc en a 27. */
export const OrgNiveau = styled.div`
    font-size: ${(p) => p.theme.type.label};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${(p) => p.theme.text3};
    margin: 0 0 7px;
`;

export const OrgGrille = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(232px, 1fr));
    gap: 8px;
`;

/* Le trait qui relie deux niveaux. Un chevron seul flotte ; un trait vertical
   court dit la subordination sans dessiner un arbre complet. */
export const OrgLien = styled.div`
    width: 1px;
    height: 14px;
    margin: 6px 0 6px 18px;
    background: ${(p) => p.theme.borderStrong};
`;

/* Carte d'une personne. Cliquable quand on connaît son UPN — c'est la clé de
   sa fiche —, inerte sinon : un rattaché sans UPN ne mène nulle part. */
export const PersonneCarte = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 11px;
    background: ${(p) => p.theme.surface};
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius.sm};
    text-decoration: none;
    color: inherit;
    min-width: 0;

    &[href] {
        transition: border-color 0.12s ease, background 0.12s ease;
    }
    &[href]:hover {
        border-color: ${(p) => p.theme.accent};
        background: ${(p) => p.theme.accentSoft};
    }
    &[href]:focus-visible {
        outline: 2px solid ${(p) => p.theme.accent};
        outline-offset: 1px;
    }

    .identite {
        min-width: 0;
    }
    .nom {
        display: block;
        font-weight: 700;
        font-size: 0.82rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .sous {
        display: block;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

/* ─── Répartition des verdicts d'une stratégie ───
   Même principe que InstallBar pour les déploiements : une barre vaut mieux que
   quatre nombres quand on balaie une liste de trente stratégies. Le gris n'est
   pas un échec — « non applicable » veut dire que la stratégie ne concerne pas
   ce poste, et c'est le cas de 291 couples du parc. */
export const BarreConformite = styled.div`
    display: flex;
    height: ${(p) => (p.$haute ? '9px' : '5px')};
    margin-top: 5px;
    border-radius: ${(p) => p.theme.radius.pill};
    overflow: hidden;
    background: ${(p) => p.theme.surface3};

    span { height: 100%; }
    span.conforme { background: ${(p) => p.theme.ok}; }
    span.nonConforme { background: ${(p) => p.theme.danger}; }
    span.erreur { background: ${(p) => p.theme.warn}; }
    span.nonApplicable { background: ${(p) => p.theme.borderStrong}; }
`;

/* ═══ Panneau de détail d'une stratégie ═══
   Le rail fait 540 px. Un tableau à quatre colonnes y portait des identifiants
   de 80 caractères — « AndroidDeviceOwnerCompliancePolicy.Advanced… » — et
   repliait chaque ligne sur trois hauteurs. Le détail est désormais composé en
   blocs empilés, lisibles de haut en bas : la synthèse, les paramètres, puis
   les postes. */

// Légende de la barre de répartition : une pastille et un nombre par verdict.
export const LegendeVerdicts = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 8px;
    font-size: ${(p) => p.theme.type.caption};
    color: ${(p) => p.theme.text2};

    span { display: inline-flex; align-items: center; gap: 5px; }
    b { color: ${(p) => p.theme.text}; font-variant-numeric: tabular-nums; }
    i {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        display: inline-block;
    }
    i.conforme { background: ${(p) => p.theme.ok}; }
    i.nonConforme { background: ${(p) => p.theme.danger}; }
    i.erreur { background: ${(p) => p.theme.warn}; }
    i.nonApplicable { background: ${(p) => p.theme.borderStrong}; }
`;

// Le chiffre qu'on vient chercher en ouvrant le panneau.
export const ChiffreCle = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: ${(p) => p.theme.radius.sm};
    background: ${(p) => (p.$tone === 'ok' ? p.theme.okSoft : p.theme.dangerSoft)};
    color: ${(p) => (p.$tone === 'ok' ? p.theme.ok : p.theme.danger)};

    strong {
        font-size: 1.35rem;
        font-weight: 800;
        line-height: 1;
        font-variant-numeric: tabular-nums;
    }
    span { font-weight: 700; font-size: 0.82rem; }
    small {
        flex-basis: 100%;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text2};
        font-weight: 400;
    }
`;

export const TitreSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 20px 0 8px;
    font-size: ${(p) => p.theme.type.label};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${(p) => p.theme.text3};
`;

// Un paramètre : son libellé lisible, son nom technique dessous, son taux
// d'échec à droite.
export const ParametreLigne = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 4px 12px;
    align-items: center;
    padding: 9px 0;
    border-bottom: 1px solid ${(p) => p.theme.border};

    &:last-child { border-bottom: none; }

    .libelle { font-weight: 600; font-size: 0.82rem; color: ${(p) => p.theme.text}; }
    .technique {
        grid-column: 1;
        font-family: 'Cascadia Code', Consolas, monospace;
        font-size: 0.7rem;
        color: ${(p) => p.theme.text3};
        overflow-wrap: anywhere;
    }
    .ratio {
        grid-row: 1 / span 2;
        grid-column: 2;
        text-align: right;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
        font-variant-numeric: tabular-nums;
        min-width: 92px;
    }
    .ratio b { font-size: 0.9rem; color: ${(p) => (p.$echec ? p.theme.danger : p.theme.ok)}; }
`;

export const RatioBarre = styled.div`
    height: 4px;
    margin-top: 4px;
    border-radius: ${(p) => p.theme.radius.pill};
    background: ${(p) => p.theme.surface3};
    overflow: hidden;

    span {
        display: block;
        height: 100%;
        background: ${(p) => p.theme.danger};
    }
`;

// Un poste : trois lignes au lieu de quatre colonnes serrées.
export const PosteLigne = styled.div`
    padding: 10px 12px;
    border: 1px solid ${(p) => p.theme.border};
    border-left: 3px solid ${(p) => (p.$echec ? p.theme.danger : p.theme.border)};
    border-radius: ${(p) => p.theme.radius.sm};
    background: ${(p) => p.theme.surface};
    margin-bottom: 6px;

    &:last-child { margin-bottom: 0; }

    .tete {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .nom {
        font-family: 'Cascadia Code', Consolas, monospace;
        font-size: 0.8rem;
        font-weight: 700;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .verdict { margin-left: auto; flex-shrink: 0; }
    .identite {
        margin-top: 2px;
        font-size: ${(p) => p.theme.type.caption};
        color: ${(p) => p.theme.text3};
        overflow-wrap: anywhere;
    }
    .echecs {
        margin: 7px 0 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .echecs li {
        display: flex;
        align-items: baseline;
        gap: 7px;
        font-size: 0.78rem;
        color: ${(p) => p.theme.text};
    }
    .echecs li i { color: ${(p) => p.theme.danger}; font-size: 0.9rem; flex-shrink: 0; }
    .echecs li.erreur i { color: ${(p) => p.theme.warn}; }
    .echecs .valeur {
        font-family: 'Cascadia Code', Consolas, monospace;
        font-size: 0.72rem;
        color: ${(p) => p.theme.text2};
        background: ${(p) => p.theme.surface3};
        padding: 0 5px;
        border-radius: 3px;
    }
    .contredit {
        margin-top: 6px;
        font-size: 0.72rem;
        color: ${(p) => p.theme.accentText};
    }
`;
